import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  CalendarCheck,
  CheckSquare,
  CircleDollarSign,
  PhoneCall,
  Send,
  TrendingUp,
  Users,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import type { BootstrapConfig, CrmRecord } from "@/lib/types";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface DashboardPageProps {
  config: BootstrapConfig;
}

const salesStages = [
  { id: "new", label: "New", color: "bg-sky-500" },
  { id: "contacted", label: "Contacted", color: "bg-cyan-500" },
  { id: "site_visit", label: "Site Visit", color: "bg-amber-500" },
  { id: "quote_sent", label: "Quote", color: "bg-orange-500" },
  { id: "negotiation", label: "Negotiation", color: "bg-purple-500" },
  { id: "won", label: "Won", color: "bg-emerald-600" },
  { id: "installation", label: "Installation", color: "bg-teal-600" },
];

function todayIsoDate(): string {
  const date = new Date();
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
}

function money(value: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
}

function recordNumber(record: CrmRecord, key: string): number {
  const value = Number(record[key] ?? record.custom?.[key] ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function stageCount(contacts: CrmRecord[], stage: string): number {
  return contacts.filter((contact) => String(contact.status ?? "") === stage).length;
}

function StatCard({
  label,
  value,
  detail,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string | number;
  detail: string;
  icon: typeof Activity;
  accent: string;
}): import("react").ReactElement {
  return (
    <Card className="overflow-hidden">
      <CardContent className="relative min-h-32">
        <div className={cn("absolute right-0 top-0 h-full w-1.5", accent)} />
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="mt-2 text-3xl font-semibold tracking-tight">{value}</div>
            <div className="mt-2 text-sm text-muted-foreground">{detail}</div>
          </div>
          <div className="rounded-md border bg-white p-2 shadow-sm">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function DashboardPage({ config }: DashboardPageProps): import("react").ReactElement {
  const stats = useQuery({ queryKey: ["crm-stats"], queryFn: adminApi.stats });
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: adminApi.sessions });
  const contacts = useQuery({ queryKey: ["crm", "contacts", "dashboard"], queryFn: () => adminApi.list("contacts", "") });
  const tasks = useQuery({ queryKey: ["crm", "tasks", "dashboard"], queryFn: () => adminApi.list("tasks", "") });

  const contactRows = contacts.data?.rows ?? [];
  const taskRows = tasks.data?.rows ?? [];
  const today = todayIsoDate();
  const openLeadCount = contactRows.filter((contact) => !["won", "lost"].includes(String(contact.status ?? ""))).length;
  const overdueTasks = taskRows.filter((task) => String(task.status ?? "open") !== "completed" && String(task.due_date ?? "") < today && task.due_date);
  const dueTodayTasks = taskRows.filter((task) => String(task.status ?? "open") !== "completed" && String(task.due_date ?? "") === today);
  const quoteValue = contactRows
    .filter((contact) => ["quote_sent", "negotiation"].includes(String(contact.status ?? "")))
    .reduce((total, contact) => total + recordNumber(contact, "estimated_value"), 0);
  const wonValue = contactRows
    .filter((contact) => String(contact.status ?? "") === "won")
    .reduce((total, contact) => total + recordNumber(contact, "estimated_value"), 0);

  return (
    <div className="space-y-7">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{config.branding.startupBanner}</h1>
          <p className="text-sm text-muted-foreground">Sales pipeline, follow-ups, and team ownership for Eagle Envitech.</p>
        </div>
        <div className="rounded-full border bg-white px-4 py-2 text-sm font-medium shadow-sm">
          {sessions.data?.count ?? "-"} live assistant session{sessions.data?.count === 1 ? "" : "s"}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Open Leads"
          value={openLeadCount || stats.data?.stats.contacts || "-"}
          detail="Active enquiries across sales stages"
          icon={Users}
          accent="bg-sky-500"
        />
        <StatCard
          label="Due Today"
          value={dueTodayTasks.length}
          detail="Calls, visits, and quote follow-ups"
          icon={CalendarCheck}
          accent="bg-amber-500"
        />
        <StatCard
          label="Overdue"
          value={overdueTasks.length}
          detail="Needs manager attention"
          icon={AlertTriangle}
          accent="bg-red-500"
        />
        <StatCard
          label="Quote Value"
          value={money(quoteValue)}
          detail="Quote sent and negotiation stages"
          icon={CircleDollarSign}
          accent="bg-orange-500"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
        <Card>
          <CardContent>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold">Pipeline Progress</h2>
                <p className="text-sm text-muted-foreground">Lead movement from first enquiry to installation handoff.</p>
              </div>
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-3">
              {salesStages.map((stage) => {
                const count = stageCount(contactRows, stage.id);
                const percent = Math.min(100, Math.round((count / Math.max(contactRows.length, 1)) * 100));
                return (
                  <div key={stage.id} className="grid grid-cols-[120px_1fr_40px] items-center gap-3 text-sm">
                    <div className="font-medium">{stage.label}</div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div className={cn("h-full rounded-full", stage.color)} style={{ width: `${Math.max(percent, count > 0 ? 10 : 0)}%` }} />
                    </div>
                    <div className="text-right font-semibold">{count}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Won Pipeline</div>
                <CheckSquare className="h-4 w-4 text-emerald-700" />
              </div>
              <div className="text-2xl font-semibold">{money(wonValue)}</div>
              <p className="text-sm text-muted-foreground">Closed solar opportunities ready for delivery tracking.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Next Calls</div>
                <PhoneCall className="h-4 w-4 text-primary" />
              </div>
              {(dueTodayTasks.length > 0 ? dueTodayTasks : taskRows.slice(0, 3)).slice(0, 3).map((task) => (
                <div key={task.id} className="rounded-md border px-3 py-2 text-sm">
                  <div className="font-medium">{String(task.title ?? "Follow-up")}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {String(task.contact_name ?? "No contact linked")} · {String(task.assignee ?? "Unassigned")}
                  </div>
                </div>
              ))}
              {taskRows.length === 0 && <div className="rounded-md border border-dashed px-3 py-5 text-center text-sm text-muted-foreground">No follow-ups yet.</div>}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Telegram Ready</div>
                <div className="mt-1 text-sm text-muted-foreground">Sales users can create leads and follow-ups from chat.</div>
              </div>
              <Send className="h-5 w-5 text-primary" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
